import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TennisBall, CheckCircle } from "@phosphor-icons/react";

export default function PublicInviteAccept() {
  const { token } = useParams();
  const nav = useNavigate();
  const { setUser } = useAuth();

  const [invite, setInvite] = useState(null);
  const [clubName, setClubName] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api
      .get(`/public/invites/${token}`)
      .then((r) => {
        setInvite(r.data.invite);
        setClubName(r.data.club_name);
        setName(r.data.invite.parent_name || "");
      })
      .catch((e) => setErr(formatApiError(e.response?.data?.detail) || "Invite not found"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return setErr("Password must be at least 8 characters.");
    if (password !== confirm) return setErr("Passwords do not match.");
    setBusy(true);
    setErr("");
    try {
      const res = await api.post(`/public/invites/${token}/accept`, { name, password });
      if (res.data.access_token) localStorage.setItem("tcd_token", res.data.access_token);
      setUser(res.data.user);
      setDone(true);
      setTimeout(() => nav(["head_coach", "coach"].includes(res.data.user?.role) ? "/dashboard" : "/parent"), 1200);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (err && !invite) {
    return (
      <div className="auth-bg min-h-screen flex items-center justify-center px-4">
        <div className="surface p-8 max-w-md text-center" data-testid="invite-error">
          <h1 className="font-heading uppercase text-2xl mb-2">Invite unavailable</h1>
          <p className="text-zinc-400 text-sm">{err}</p>
        </div>
      </div>
    );
  }
  if (!invite) return <div className="auth-bg min-h-screen flex items-center justify-center font-label">Loading invite…</div>;

  const isHeadCoach = invite.kind === "head_coach";
  const isStaff = invite.kind === "staff";
  const staffRoleLabel = invite.title || (invite.role === "head_coach" ? "admin" : invite.role || "coach");
  const subtitle = isHeadCoach
    ? <>You&apos;ve been invited to run <span className="text-white font-semibold">{clubName}</span> on courtsideOS. Set a password to activate your admin account.</>
    : isStaff
    ? <>You&apos;ve been invited to join <span className="text-white font-semibold">{clubName}</span> as <span className="text-white font-semibold">{staffRoleLabel}</span>. Set a password to activate your account.</>
    : <>You&apos;ve been invited to follow <span className="text-white font-semibold">{invite.player_name}</span>&apos;s tennis progress. Set a password to activate your account.</>;
  const eyebrow = isHeadCoach ? "admin activation" : isStaff ? "staff activation" : "parent activation";

  return (
    <div className="auth-bg min-h-screen flex items-center justify-center px-4 py-10" data-testid="invite-accept-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-md bg-[#dfff00] flex items-center justify-center">
            <TennisBall size={22} weight="duotone" color="#09090b" />
          </div>
          <div>
            <div className="font-heading uppercase tracking-tight text-2xl leading-none">
              <span className="text-white">court</span><span className="text-[#dfff00]">side</span><span className="text-[#dfff00]">OS</span>
            </div>
            <div className="font-label mt-1">{eyebrow}</div>
          </div>
        </div>

        <div className="surface p-7">
          <div className="font-label">{clubName}</div>
          <h1 className="font-heading uppercase text-3xl mt-1">Welcome.</h1>
          <p className="text-zinc-400 text-sm mt-2 mb-5">{subtitle}</p>

          {done ? (
            <div className="text-center py-6" data-testid="invite-success">
              <CheckCircle size={48} weight="fill" className="text-[#16ec06] mx-auto" />
              <div className="font-heading uppercase text-xl mt-3">All set</div>
              <div className="text-zinc-400 text-sm">Taking you to your dashboard…</div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="invite-form">
              <div>
                <label className="font-label block mb-1.5">Your name</label>
                <input required className="input" value={name} onChange={(e) => setName(e.target.value)} data-testid="invite-name" />
              </div>
              <div>
                <label className="font-label block mb-1.5">Email</label>
                <input disabled className="input opacity-60" value={invite.parent_email || ""} />
              </div>
              <div>
                <label className="font-label block mb-1.5">Choose a password</label>
                <input required type="password" minLength={8} className="input" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="invite-password" />
              </div>
              <div>
                <label className="font-label block mb-1.5">Confirm password</label>
                <input required type="password" className="input" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="invite-confirm" />
              </div>
              {err && <div className="text-sm text-[#ff0026] bg-[#ff0026]/10 border border-[#ff0026]/30 rounded-md p-3" data-testid="invite-err">{err}</div>}
              <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="invite-submit-btn">
                {busy ? "Activating…" : "Activate my account"}
              </button>
            </form>
          )}
        </div>
        <p className="text-xs text-zinc-600 mt-6 text-center">
          {isHeadCoach || isStaff
            ? <>By activating, you agree to administer {clubName} on courtsideOS in line with the platform&apos;s terms.</>
            : <>By activating, you consent to {clubName} processing your child&apos;s tennis-related data. You can withdraw at any time.</>}
        </p>
      </div>
    </div>
  );
}
